<script>
  import {
    scaleLinear,
    scaleOrdinal,
    scaleSequential,
    scaleThreshold
  } from "d3-scale";
  import { csvParse, autoType } from "d3-dsv";
  import { geoData } from "./bangalore_data.js";
  import {
    createGeoScales,
    DiscreteLegend,
    Graphic,
    Grid,
    Label,
    Line,
    LineLayer,
    PointLayer,
    PolygonLayer,
    RectangleLayer,
    Section,
    Title,
    XAxis,
    YAxis
  } from "@snlab/florence";
  import DataContainer from "@snlab/florence-datacontainer";
  import {
    classificationMethods,
    colourSchemes,
    binsToThreshold,
    join,
    mapOptions
  } from "./utils/utils.js";
  import MapWithFlorence from "./components/MapWithFlorence.svelte";
  import {
    projectMapboxMercator,
    projectBbox
  } from "./utils/mapbox-florence-bridge-utils.js";

  // overall settings
  const color = "rgb(187, 67, 48)";
  const padding = { left: 20, bottom: 10, top: 10, right: 20 };

  // Interactive map
  // map related init constants
  const width = "100%";
  const height = "100%";
  let viewBoxWidth = 750;
  let viewBoxHeight = 600;
  const center = [77.5946, 12.9716];
  const zoom = 10.5;

  // scale vars
  let myGeoScaleInit;
  let originalBbox;
  let scaleX;
  let scaleY;
  let zoomIdentity = { x: 0, y: 0, kx: 1, ky: 1 };

  // All variables used

  // Empty Variables
  let userData;
  let waterData;
  let joinedData;
  let joinedDataProjected;
  let columnNames;

  let myColorScale;
  $: myColorScale;

  let columnDomain;
  let binMethod;
  $: binMethod;
  let numClasses;
  let classBins;

  let minBin = 3;
  let maxBin = 9;

  let numBinSelected = minBin;
  $: numBinSelected;

  let rangeSelected;
  $: rangeSelected;

  let classThresholds;
  $: classThresholds;

  let wardList;

  // Declared Variables
  let done = false;

  // Load interactive map
  // functions to create scales and update bounds for 'zoom' implementation
  function setScalesZoom(event) {
    const mapInstance = event.detail;
    const bbox = projectBbox(mapInstance.getBounds());
    myGeoScaleInit = createGeoScales(bbox);
    originalBbox = bbox;
    scaleX = myGeoScaleInit.scaleX.range([0, viewBoxWidth]);
    scaleY = myGeoScaleInit.scaleY.range([0, viewBoxHeight]);
  }

  function updateZoom(event) {
    calculateZoomIdentity(
      originalBbox,
      projectBbox(event.detail),
      zoomIdentity
    );
  }

  function calculateZoomIdentity(original, current) {
    const deltaX = scaleX(original.x[0]) - scaleX(current.x[0]);
    const deltaY = scaleY(original.y[0]) - scaleY(current.y[0]);
    const zoom =
      (original.x[1] - original.x[0]) / (current.x[1] - current.x[0]);
    const zoomId = { x: deltaX * zoom, y: deltaY * zoom, kx: zoom, ky: zoom };
    zoomIdentity = zoomId;
  }

  // Load static map info

  const wardGeoData = new DataContainer(geoData);
  let myGeoScale = createGeoScales(wardGeoData.domain("$geometry"));
  let wardlist = wardGeoData.domain("ward_name").sort();

  const wardGeoDataProjected = wardGeoData.reproject(projectMapboxMercator);

  // Reactive Variables

  // Event Handler Variables

  let selectedWard = ""; // filter variable

  let mapOption = mapOptions.option;
  $: mapOption;

  let selectedMapOption = mapOptions[0];
  $: selectedMapOption;

  $: {
    if (selectedMapOption == mapOptions[1]) {
      getData();
    }
    else if(selectedMapOption == mapOptions[2]) {
      console.log("Waiting for user input")
    }
  }

  // Column selectedMapOption options

  let columnSelected;
  $: columnSelected = "b_tot";

  // Classification method options

  let selectedMethod;
  $: selectedMethod = classificationMethods[0];

  $: {
    if (
      selectedMethod != classificationMethods[0] ||
      numBinSelected != minBin ||
      selectedColourScheme != colourSchemes[0]
    ) {
      binMethod = {
        groupBy: columnSelected,
        method: selectedMethod.option,
        numClasses: numBinSelected
      };

      classBins = joinedData.bin(binMethod).column("bins");

      rangeSelected = selectedColourScheme.option;


      classThresholds = binsToThreshold(classBins);
      myColorScale = scaleThreshold()
        .domain(classThresholds)
        .range(rangeSelected[numBinSelected]);

    }
  }

  // Colour Schemes

  // let colourScheme = colourSchemes.option;

  let selectedColourScheme;
  $: selectedColourScheme = colourSchemes[0];

  let opacity = 1;

  // Functions

// Function getData() obtains the data needed for the map with the sample dataset
  async function getData() {
    await Promise.all([
      loadCSV("data/blore_water_data.csv")
    ]);
    waterData.deleteColumn("ward_name"); // already exists in geo data

    // joining waterdata to geo data
    const columnsToJoin = join(wardGeoData, waterData, ["ward_no", "ward_no"]);

    // copy geo data so we can keep a simple, 'pure' version around
    joinedData = Object.create(wardGeoData);
    for (const columnName in columnsToJoin) {
      joinedData.addColumn(columnName, columnsToJoin[columnName]);
    }

    // column options for customization
    columnNames = joinedData.columnNames();

    columnDomain = joinedData.domain(columnSelected);

    // classification and binning
    
    binMethod = {
      groupBy: columnSelected,
      method: selectedMethod.option,
      numClasses: numBinSelected
    };

    classBins = joinedData.bin(binMethod).column("bins");

    classThresholds = binsToThreshold(classBins);

    rangeSelected = selectedColourScheme.option;

    myColorScale = scaleThreshold()
      .domain(classThresholds)
      .range(rangeSelected[numBinSelected]);

    joinedDataProjected = joinedData.reproject(projectMapboxMercator);

    done = true;
  }

// Function getDataUser() gets the data for a data set uploaded by the user

async function getDataUser() {
    await Promise.all([
      userCSV()
    ]);

    // joining userdata to geo data
    const columnsToJoin = join(wardGeoData, userData, ["ward_no", "ward_no"]);

    // copy geo data so we can keep a simple, 'pure' version around
    joinedData = Object.create(wardGeoData);
    for (const columnName in columnsToJoin) {
      joinedData.addColumn(columnName, columnsToJoin[columnName]);
    }

    // column options for customization
    columnNames = joinedData.columnNames();

    columnDomain = joinedData.domain(columnSelected);

    // classification and binning
    

    binMethod = {
      groupBy: columnSelected,
      method: selectedMethod.option,
      numClasses: numBinSelected
    };

    classBins = joinedData.bin(binMethod).column("bins");

    classThresholds = binsToThreshold(classBins);

    rangeSelected = selectedColourScheme.option;

    myColorScale = scaleThreshold()
      .domain(classThresholds)
      .range(rangeSelected[numBinSelected]);

    joinedDataProjected = joinedData.reproject(projectMapboxMercator);

    done = true;
  }

// function for mouseover events

// For Base Map

function handleMouseover (event) {
  selectedWard = event.key
}

function handleMouseout (event) {
  selectedWard = ''
}

// For Sample Map

let strokeWidth = 0;
$: strokeWidth;

let joinedDataFilter;
$: joinedDataFilter

function handleMouseoverSample (event) {
  selectedWard = event.key
  strokeWidth = 0.2;
}

function handleMouseoutSample (event) {
  selectedWard = ''
  strokeWidth = 0;
}

$: {
  if (selectedMapOption == mapOptions[1]) {
    if (selectedWard === ""){
    joinedDataFilter = joinedData;
  } else {
    joinedDataFilter = joinedData.filter(row => row.ward_name === selectedWard)
  }
  }
  
}

// For Custom Map

function handleMouseoverCustom (event) {
  selectedWard = event.key
  strokeWidth = 0.2;
}

function handleMouseoutCustom (event) {
  selectedWard = ''
  strokeWidth = 0;
}

// async function to fetch and parse water data
  async function loadCSV(url) {
    const response = await fetch(url);
    const data = await response.text();
    const dataParsed = csvParse(data, autoType);
    waterData = new DataContainer(dataParsed);
  }

// async function to load user data

  async function userCSV(){

    const fileReader = new FileReader();
    const selectedFile = fileReader.readAsText(input)
    const selectedFileParsed = csvParse(selectedFile, autoType);
    userData = new DataContainer(selectedFileParsed);
  } 

let userText;
let userTextInput;

$: {
  if(selectedMapOption == mapOptions[2]){
    let userText = [];
    let userTextInput = document.getElementById("user-input-text")

    // window.onload = function() {
    //   document.getElementById("submit-user-input-text").addEventListener('click', insert)
    //   console.log('Ta da.')
    // }
  }
}

function insert() {
      userText.push(userTextInput.value);
      console.log(userText)
    }

</script>

<style>
  /* Header */
  header {
    /* margin-bottom: 0px; */
    border-bottom: 2px solid #000;
    overflow: auto;
    margin-top: 0;
  }

  header > h1 {
    font-family: "Bebas Neue", cursive;
    font-size: 350%;
    margin: 0;
    float: left;
  }

  /* Body */
  .ward-name-text{
    padding-left: 1%;
    border-width: 5px;
    border-left-style: solid;
  }

  .row-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  .main-content {
    display: flex;
    flex-direction: row;
    flex: 1;
  }

  .mapWithOverlay {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .map {
    width: 100%;
    height: 100%;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    width: 100%;
    height: 100%;
  }

  .overlay :global(svg) {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }

  .toolbar-left {
    max-width: 24%;
    width: 24%;
    padding-right: 1%;
  }

  .graph {
    flex: 1;
  }

  .toolbar-right {
    max-width: 24%;
    width: 24%;
    padding-left: 1%;
  }

  label {
    font-family: "Hind", sans-serif;
    font-weight: 700;
    white-space: pre-wrap;
    width: 100px;
  }

  #column-select, #colour-select, #class-select, #number-of-classes, #opacity {
    width: 150px;
  }

  #user-input-text{
    width: 100%;
  }

  p {
    font-family: "Hind", sans-serif;
  }

  ul {
    font-family: "Hind", sans-serif;
  }

  /* Footer */
  footer {
    font-family: "Hind", sans-serif;
    border-top: 2px solid #000;
    /* overflow: auto; */
  }

  /* reactivity */

  @media screen and (max-width: 500px) {
    header {
      float: none;
      display: block;
      text-align: left;
      font-size: 300%;
    }

    footer {
      float: bottom;
      text-align: left;
    }
  }
</style>

<head>
  <meta charset="UTF-8" />
  <title>Bangalore: An Interactive Visualisation Tool</title>
  <link
    href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Hind:wght@300&display=swap"
    rel="stylesheet" />
</head>

<div class="row-container">
  <header>
    <h1>Visualize Bangalore: An Interactive Tool</h1>
  </header>
  <div class="main-content">
    <!-- Toolbar Left -->
    <div class="toolbar-left">
      <p>
        Use this tool to visualize spatial data about Bangalore, India.
        <br />
        <br />
        Test this tool out using the sample dataset
        provided. Use the drop down menu & select 'Sample Dataset' to begin.
        <br />
        <br />
        Alternatively, upload your own dataset. Begin by using the drop down menu & selecting 'Custom Dataset'.
        <br />
        <br />
      </p>
      <!-- Interactive display of ward info -->
      <div class="ward-name-text">
      
        {#if selectedWard === ''}
          <label> {'Hover over the map for details about the selected ward.'} </label>
        {:else}
          <label> {selectedWard} </label>
        {/if}
        
      </div>
      <!-- Select dataset -->
      <div>
        <br />
        <br />
        <br />
        <label for="map-select">Choose a dataset to visualize:</label>
        <span style="display:inline-block; width: 1px;" />
        <select bind:value={selectedMapOption} id="map-select">
          {#each mapOptions as mapOption}
            <option value={mapOption}>{mapOption.title}</option>
          {/each}
        </select>
      </div>
      {#if selectedMapOption == mapOptions[1]}
        <ul type="square">
          <p>
            <br />
            The sample dataset provides details of the water tax billed and
            collected in the city between the years 2013 and 2014. This data is
            broken down further into 2 categories: residential and commercial.
          </p>
        </ul>
      {:else if selectedMapOption == mapOptions[2]}
        <br />
          <p>Before uploading, please ensure that the dataset is</p>
        <ul type="square">
        <li>organized by administrative wards,</li>
        <br />
        <li>
          and that the dataset contains either the administrative ward numbers or
          names. 
        </li>
      </ul>
        <br />
        <label for="file-upload">Choose a CSV file to upload:</label><span style="display:inline-block; width: 15px;" /><input type="file" id="user-file-input" name="user-file-input" accept=".csv">
        <br />
        <br />
        <p>Or paste the contents of your dataset into the text box below in CSV format:</p>
        <form id="user-input">
          <textarea id="user-input-text"></textarea>
          <input type="button" id="submit-user-input-text" name="Submit" value="Submit" onclick="insert()" >
        </form>
      {/if}
    </div>

    <!-- Main Map -->
    <div class="graph">
      <div class="mapWithOverlay">
        <div class="map">
          <MapWithFlorence
            on:load={setScalesZoom}
            on:pan={updateZoom}
            on:zoom={updateZoom}
            on:resize={setScalesZoom}
            {center}
            {zoom}
            {width}
            {height} />
        </div>
        <div
          class="overlay"
          bind:clientWidth={viewBoxWidth}
          bind:clientHeight={viewBoxHeight}>
          <Graphic width={viewBoxWidth} height={viewBoxHeight}>
            <!-- Map with custom data -->
            {#if selectedMapOption == mapOptions[2]}
              <Section {...myGeoScaleInit} {zoomIdentity}>
                <PolygonLayer
                  geometry={wardGeoDataProjected.column('$geometry')}
                  fill={'white'}
                  fillOpacity={0}
                  stroke={'black'}
                  strokeWidth={1}
                  key={wardGeoDataProjected.column('ward_name')}
                  onMouseover={handleMouseoverCustom}
                  onMouseout={handleMouseoutCustom}
                   />
                <!-- <PolygonLayer
                    geometry={joinedDataProjected.column('$geometry')}
                    fill={joinedDataProjected.map(columnSelected, myColorScale)}
                    fillOpacity={opacity}
                    stroke={'black'}
                    strokeWidth={1}
                     /> -->
              </Section>
              <!-- Map with sample data -->
            {:else if selectedMapOption == mapOptions[1]}
              {#if done}
                <Section {...myGeoScaleInit} {zoomIdentity}>
                  <PolygonLayer
                    geometry={wardGeoDataProjected.column('$geometry')}
                    fill={'white'}
                    fillOpacity={0}
                    stroke={'black'}
                    strokeWidth={1}
                     />
                  <PolygonLayer
                    geometry={joinedDataProjected.column('$geometry')}
                    fill={joinedDataProjected.map(columnSelected, myColorScale)}
                    fillOpacity={opacity}
                    stroke={'black'}
                    strokeWidth={1}
                    key={joinedDataProjected.column('ward_name')}
                    onMouseover={handleMouseoverSample}
                    onMouseout={handleMouseoutSample} />
                </Section>
              {/if}
              <!-- Base map -->
            {:else}
              <Section {...myGeoScaleInit} {zoomIdentity}>
                <PolygonLayer
                  geometry={wardGeoDataProjected.column('$geometry')}
                  fill={'white'}
                  fillOpacity={0}
                  stroke={'black'}
                  strokeWidth={1}
                  key={wardGeoDataProjected.column('ward_name')}
                  onMouseover={handleMouseover}
                  onMouseout={handleMouseout} />

              </Section>
            {/if}

          </Graphic>

        </div>
      </div>
    </div>

    <!-- Toolbar-right -->

    <div class="toolbar-right">
      <div>
        <!-- if statement displays conditionally -->
        {#if selectedMapOption == mapOptions[1]}
          <!-- ensures the data is loaded before making the options active -->
          {#if done}
            <p>Customization Options:</p>
            <br />
            <!-- Column selectedMapOption -->
            <label for="column-select">Column to visualize:<span style="display:inline-block; width: 25px;" /></label>
            <select bind:value={columnSelected} id="column-select">
              {#each columnNames as columnName}
                <option value={columnName}>{columnName}</option>
              {/each}
            </select>
            <br />
            <br />
            <br />
            <!-- Classification selectedMapOption -->
            <label for="class-select">Classification method:<span style="display:inline-block; width: 10px;" /></label>
            <select bind:value={selectedMethod} id="class-select">
              {#each classificationMethods as classificationMethod}
                <option value={classificationMethod}>
                  {classificationMethod.title}
                </option>
              {/each}
            </select>
            <br />
            <br />
            <br />
            <!-- Number of Classes -->
            <label for="number-of-classes">Number of classes:<span style="display:inline-block; width: 5px;" /></label><span style="display:inline-block; width: 12px;" />{numBinSelected}

            <input
              type="range"
              min={minBin}
              max={maxBin}
              bind:value={numBinSelected}
              id="number-of-classes" />
            <br />
            <br />
            <br />
            <!-- Colour selectedMapOption -->
            <label for="colour-select">Colour scheme:<span style="display:inline-block; width: 56px;" /></label>
            <select bind:value={selectedColourScheme} id="colour-select">
              {#each colourSchemes as colourScheme}
                <option value={colourSchemes.option}>{colourScheme.title}</option>
              {/each}
            </select>
            <br />
            <br />
            <br />
            <!-- Opacity -->
            <label for="opacity">Fill opacity:</label><span style="display:inline-block; width: 30px;" />{opacity}
            
            <span style="display:inline-block; width: 42px;" />

            <input
              type="range"
              min={0}
              max={1}
              step="0.1"
              bind:value={opacity}
              id="opacity" />
            <br />
            <br />
            <br />
            <!-- Scatterplot -->
            <Graphic {width} {height} >
              <Title title="Scatterplot of Ward vs. Selected Column" titleFontSize={3.5} usePadding={true} titleFontFamily={'Hind'} />
              <Section
                padding={15}
                flipY
                scaleX={scaleLinear().domain(joinedData.domain('ward_no'))}
                scaleY={scaleLinear().domain(joinedData.domain(columnSelected))}>
                <PointLayer
                  x={joinedDataFilter.column('ward_no')}
                  y={joinedDataFilter.column(columnSelected)}
                  fill={joinedDataProjected.map(columnSelected, myColorScale)} 
                  fillOpacity={opacity}
                  radius={0.8}
                  stroke={'black'}
                  strokeWidth={strokeWidth}
                   />
                <XAxis title={'Ward No'} titleFontSize={3} labelFontSize={2} tickSize={1} baseLineWidth={0.5} />
                <YAxis titleFontSize={3} labelFontSize={2} tickSize={1} baseLineWidth={0.5} />
              </Section>
              
            </Graphic>
          {/if}
        {/if}
      </div>
    </div>
  </div>
  <footer>
    Developed by Anirudh Govind as part of the Interactive Data Visualization Course in the MUSPP Programme at SUTD (2019-2020).
  </footer>
</div>
